import React, { useEffect, useMemo, useState } from "react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Progress } from "./ui/progress";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { ArrowLeft, Menu, X, HelpCircle } from "lucide-react";
import { QuestionPalette } from "./ui/QuestionPalette";
import { getLogicTest, submitLogicResponse, submitLogicTest } from "../services/api";
import login_hero_image from '../assets/eko-_transparent-removebg-preview.png';
import TestBadge from './TestBadge';

/* ----------------------------- Types ----------------------------- */
interface LogicOption {
  index: number;
  text: string;
  image_url: string | null;
}

interface LogicItem {
  item_id: string;
  item_number: string;
  item_type: string;
  question_text: string;
  difficulty: string;
  options: LogicOption[];
  image_url?: string;
}

type ResultRow = {
  item_id: string;
  item_number: string;
  question_text: string;
  selected_answer_index: number | null;
  selected_answer: string;
  is_correct: boolean;
  response_time_seconds: number;
  feedback: string;
  correct_answer_index?: number;
  correct_answer?: string;
};

interface LogicalTestInterfaceProps {
  onComplete: (results: { responses: ResultRow[]; summary: any }) => void;
  onBackToDashboard: () => void;
  playerName: string;
  gradeName: string;
  childId: string;
}

export const LogicalTestInterface: React.FC<LogicalTestInterfaceProps> = ({
  onComplete,
  onBackToDashboard,
  playerName,
  gradeName,
  childId,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [items, setItems] = useState<LogicItem[]>([]);
  const [testId, setTestId] = useState<string>("");
  const [results, setResults] = useState<ResultRow[]>([]);
  const [attemptedIndices, setAttemptedIndices] = useState<number[]>([]);
  const [skippedIndices, setSkippedIndices] = useState<number[]>([]);
  const [selectedAnswerIndex, setSelectedAnswerIndex] = useState<number | null>(null);
  const [paletteCollapsed, setPaletteCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [startTime, setStartTime] = useState<number>(Date.now());
  const [currentQuestionTime, setCurrentQuestionTime] = useState<number>(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentItem = items[currentIndex];
  const totalItems = items.length;

  const progressPercentage = useMemo(
    () => ((currentIndex + 1) / totalItems) * 100,
    [currentIndex, totalItems]
  );

  const motivationalMessages = useMemo(() => [
    `Great start, {name}!`,
    `Awesome work, {name}!`,
    `Keep going, {name}!`,
    `You're doing great, {name}!`
  ], []);

  const firstName = useMemo(() => (playerName?.split(" ")[0] || playerName || ""), [playerName]);

  const currentMotivation = useMemo(() => {
    if (!totalItems || totalItems <= 0) return motivationalMessages[0].replace('{name}', firstName);
    const segments = motivationalMessages.length;
    const segmentSize = Math.max(1, Math.floor(totalItems / segments));
    const idx = Math.min(segments - 1, Math.floor(currentIndex / segmentSize));
    return motivationalMessages[idx].replace('{name}', firstName);
  }, [currentIndex, totalItems, motivationalMessages, firstName]);

  /* ----------------------------- Mobile/Tablet Detection ----------------------------- */
  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setIsMobile(w < 768);
      setIsTablet(w >= 768 && w < 1024);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ----------------------------- Timer ----------------------------- */
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentQuestionTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, [startTime]);

  /* ----------------------------- Fetch Test Data ----------------------------- */
  useEffect(() => {
    const fetchTest = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        const data = await getLogicTest(childId, gradeName);
        if (data.success) {
          setTestId(data.test_id);
          setItems(data.items || []);
        } else {
          setLoadError('Failed to load test');
        }
      } catch (error) {
        console.error('Error fetching test:', error);
        setLoadError(error instanceof Error ? error.message : 'Unknown error');
      } finally {
        setIsLoading(false);
      }
    };

    if (childId && gradeName) {
      fetchTest();
    }
  }, [childId, gradeName]);

  /* ----------------------------- Reset on Question Change ----------------------------- */
  useEffect(() => {
    setSelectedAnswerIndex(null);
    setStartTime(Date.now());
    setCurrentQuestionTime(0);
  }, [currentIndex]);

  /* ----------------------------- Helper Functions ----------------------------- */
  const addUnansweredItems = (currentResults: ResultRow[]): ResultRow[] => {
    const unansweredItems: ResultRow[] = [];

    for (let i = 0; i < totalItems; i++) {
      const alreadyAttempted = currentResults.some(result =>
        items[i] && result.item_id === items[i].item_id
      );

      if (!alreadyAttempted) {
        unansweredItems.push({
          item_id: items[i].item_id,
          item_number: items[i].item_number,
          question_text: items[i].question_text,
          selected_answer_index: null,
          selected_answer: "",
          is_correct: false,
          response_time_seconds: 0,
          feedback: "Not answered",
        });
      }
    }

    return [...currentResults, ...unansweredItems];
  };

  const findNextUnattemptedOrSkippedQuestion = (): number => {
    // First, look for skipped questions after current index
    for (let i = currentIndex + 1; i < totalItems; i++) {
      if (skippedIndices.includes(i) && !attemptedIndices.includes(i)) {
        return i;
      }
    }
    // Then look for unattempted questions after current index
    for (let i = currentIndex + 1; i < totalItems; i++) {
      if (!attemptedIndices.includes(i) && !skippedIndices.includes(i)) {
        return i;
      }
    }
    // If no skipped or unattempted questions after current, check from beginning for skipped
    for (let i = 0; i < currentIndex; i++) {
      if (skippedIndices.includes(i) && !attemptedIndices.includes(i)) {
        return i;
      }
    }
    // Then check for unattempted questions from beginning
    for (let i = 0; i < currentIndex; i++) {
      if (!attemptedIndices.includes(i) && !skippedIndices.includes(i)) {
        return i;
      }
    }
    return -1;
  };

  /* ----------------------------- Handlers ----------------------------- */
  const handleSelectAnswer = (optionIndex: number) => {
    setSelectedAnswerIndex(optionIndex);
  };

  const handleSubmitAnswer = async () => {
    if (selectedAnswerIndex === null || !currentItem) return;

    const timeSpent = Math.floor((Date.now() - startTime) / 1000);
    const selectedOption = currentItem.options[selectedAnswerIndex];

    try {
      // Submit response to API for immediate feedback
      const feedbackData = await submitLogicResponse(
        childId,
        currentItem.item_id,
        selectedAnswerIndex,
        timeSpent,
        1,
        false,
        ""
      );

      // Check if this question was previously skipped
      const wasSkipped = skippedIndices.includes(currentIndex);
      let newResults;

      const resultRow: ResultRow = {
        item_id: currentItem.item_id,
        item_number: currentItem.item_number,
        question_text: currentItem.question_text,
        selected_answer_index: selectedAnswerIndex,
        selected_answer: selectedOption.text,
        is_correct: feedbackData.is_correct || false,
        response_time_seconds: timeSpent,
        feedback: feedbackData.feedback || "",
        correct_answer_index: feedbackData.correct_answer_index,
        correct_answer: feedbackData.correct_answer,
      };

      if (wasSkipped) {
        newResults = results.map(result =>
          result.item_id === currentItem.item_id ? resultRow : result
        );
      } else {
        newResults = [...results, resultRow];
      }

      setResults(newResults);
      setAttemptedIndices((prev) =>
        prev.includes(currentIndex) ? prev : [...prev, currentIndex]
      );
      setSkippedIndices((prev) => prev.filter(index => index !== currentIndex));

      // Move to next question or complete test
      if (currentIndex === totalItems - 1) {
        // Submit full test
        submitFullTest(newResults);
        return;
      }

      const nextIndex = findNextUnattemptedOrSkippedQuestion();
      if (nextIndex !== -1) {
        setCurrentIndex(nextIndex);
      } else {
        submitFullTest(newResults);
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
      alert('Error submitting answer. Please try again.');
    }
  };

  const handleSkip = () => {
    const wasSkipped = skippedIndices.includes(currentIndex);
    let newResults;

    if (!wasSkipped) {
      const timeSpent = Math.floor((Date.now() - startTime) / 1000);
      const row: ResultRow = {
        item_id: currentItem.item_id,
        item_number: currentItem.item_number,
        question_text: currentItem.question_text,
        selected_answer_index: null,
        selected_answer: "",
        is_correct: false,
        response_time_seconds: timeSpent,
        feedback: "Skipped",
      };
      newResults = [...results, row];
      setResults(newResults);
      setSkippedIndices((prev) => (prev.includes(currentIndex) ? prev : [...prev, currentIndex]));
    } else {
      newResults = results;
    }

    if (currentIndex === totalItems - 1) {
      submitFullTest(newResults);
      return;
    }

    const nextIndex = findNextUnattemptedOrSkippedQuestion();
    if (nextIndex !== -1) {
      setCurrentIndex(nextIndex);
    } else {
      submitFullTest(newResults);
    }
  };

  const handleLeave = () => {
    submitFullTest(addUnansweredItems(results));
  };

  const submitFullTest = async (finalResults: ResultRow[]) => {
    try {
      setIsSubmitting(true);
      const summaryData = await submitLogicTest(
        childId,
        gradeName,
        testId,
        finalResults
          .filter(r => r.selected_answer_index !== null)
          .map(r => ({
            item_id: r.item_id,
            selected_answer_index: r.selected_answer_index as number,
            response_time_seconds: r.response_time_seconds,
          }))
      );
      onComplete({ responses: finalResults, summary: summaryData });
    } catch (error) {
      console.error('Error submitting test:', error);
      alert('Error submitting test. Please try again.');
      setIsSubmitting(false);
    }
  };

  /* ----------------------------- Render ----------------------------- */
  if (isLoading) {
    return (
      <div className="h-screen w-full bg-gradient-to-br from-peach-50 via-lavender-100 to-white flex font-[Nunito_Sans,sans-serif] items-center justify-center">
        <Card className="p-8 rounded-3xl shadow-xl bg-white/95">
          <div className="text-center">
            <TestBadge test="logic" size="lg" className="mb-3" />
            <div className="text-lg font-semibold text-[#6B6FA3] mb-4">Getting the puzzles ready…</div>
            <div className="flex justify-center mb-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8E7BFF]"></div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="h-screen w-full bg-gradient-to-br from-peach-50 via-lavender-100 to-white flex font-[Nunito_Sans,sans-serif] items-center justify-center">
        <Card className="p-8 rounded-3xl shadow-xl bg-white/95">
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600 mb-4">Error Loading Test</div>
            <div className="text-gray-600 mb-6">{loadError}</div>
            <Button
              onClick={onBackToDashboard}
              className="bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] text-white font-bold"
            >
              Back to Dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (!currentItem) {
    return (
      <div className="h-screen w-full bg-gradient-to-br from-peach-50 via-lavender-100 to-white flex font-[Nunito_Sans,sans-serif] items-center justify-center">
        <Card className="p-8 rounded-3xl shadow-xl bg-white/95">
          <div className="text-center">
            <div className="text-2xl font-bold text-[#8E7BFF] mb-4">No items found</div>
            <Button
              onClick={onBackToDashboard}
              className="bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] text-white font-bold"
            >
              Back to Dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-screen w-full bg-gradient-to-br from-peach-50 via-lavender-100 to-white flex font-[Nunito_Sans,sans-serif] flex-col">
      <div className="three-col-layout flex flex-1 gap-4 lg:gap-6 items-start unified-medium-scale">
        {/* Desktop: left question navigation */}
        <div className="left-col hidden lg:flex lg:flex-col lg:w-96 xl:w-[420px] p-4 flex-shrink-0 unified-medium-panel">
          <div className="sticky top-20">
            <div className="w-full p-4 rounded-2xl bg-gradient-to-b from-[#F3E8FF] to-white/80 shadow-md border border-gray-100 lg:origin-top-left unified-medium-inner">
              <QuestionPalette
                total={totalItems}
                currentIndex={currentIndex}
                attempted={attemptedIndices}
                skipped={skippedIndices}
                onJump={(idx) => {
                  if (skippedIndices.includes(idx) || idx === currentIndex) {
                    setCurrentIndex(idx);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                isMobile={false}
                isTablet={false}
                isDesktopLeft={true}
                collapsed={false}
              />
            </div>
          </div>
        </div>

        {/* Center Main Card */}
        <div className="center-col flex-1 flex flex-col items-center lg:origin-top-left min-w-0 unified-medium-panel">
          <div className="center-inner w-full max-w-2xl mx-auto mt-0 md:mt-4 p-2 md:p-4 unified-medium-inner flex flex-col flex-1">
            <div className="flex items-center justify-between mb-4">
              <Button
                onClick={onBackToDashboard}
                variant="outline"
                size="sm"
                className="bg-white/80 hover:bg-white"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>

              <div className="flex items-center gap-3">
                <div className="text-right">
                  <TestBadge test="logic" subject={playerName} />
                  {isMobile && <div className="text-xs text-gray-600">Question {currentIndex + 1} of {totalItems}</div>}
                </div>

                {/* Mobile Menu Button */}
                <div className="relative lg:hidden">
                  <Button
                    onClick={() => setShowMobileMenu(!showMobileMenu)}
                    variant="outline"
                    size="sm"
                    className="bg-white/80 hover:bg-white p-2"
                  >
                    <Menu className="w-4 h-4" />
                  </Button>

                  {/* Mobile Side Navigation */}
                  {showMobileMenu && (
                    <>
                      <div
                        className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
                        onClick={() => setShowMobileMenu(false)}
                      />
                      <div className={`fixed top-0 right-0 h-full w-80 bg-white z-50 transform transition-transform duration-300 ease-in-out shadow-2xl flex flex-col ${
                        showMobileMenu ? 'translate-x-0' : 'translate-x-full'
                      }`}>
                        <div className="bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] p-4 text-white flex-shrink-0">
                          <div className="flex items-center justify-between">
                            <h3 className="text-lg font-bold">Question Navigation</h3>
                            <button
                              onClick={() => setShowMobileMenu(false)}
                              className="p-2 rounded-full hover:bg-white/20 transition-colors"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                          <div className="mt-2 text-sm opacity-90">
                            Question {currentIndex + 1} of {totalItems}
                          </div>
                        </div>

                        <div className="p-4 border-b border-gray-100">
                          <button
                            onClick={() => {
                              handleLeave();
                              setShowMobileMenu(false);
                            }}
                            className="w-full py-3 rounded-xl text-white font-bold shadow-md bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] hover:brightness-95 transition-all duration-200 flex items-center justify-center gap-2"
                          >
                            <span>🏁</span>
                            <span>End Test</span>
                          </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4" style={{ maxHeight: 'calc(100vh - 200px)' }}>
                          <div className="bg-gradient-to-b from-[#F3E8FF] to-white/80 rounded-xl p-4 shadow-sm">
                            <QuestionPalette
                              total={totalItems}
                              currentIndex={currentIndex}
                              attempted={attemptedIndices}
                              skipped={skippedIndices}
                              onJump={(idx) => {
                                if (skippedIndices.includes(idx) || idx === currentIndex) {
                                  setCurrentIndex(idx);
                                  setShowMobileMenu(false);
                                  window.scrollTo({ top: 0, behavior: 'smooth' });
                                }
                              }}
                              isMobile={true}
                              isTablet={false}
                              isDesktopLeft={false}
                              collapsed={false}
                            />
                          </div>

                          <div className="mt-6 space-y-4">
                            <div className="bg-blue-50 rounded-xl p-4">
                              <h4 className="font-semibold text-[#55608A] mb-2">Test Progress</h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Completed:</span>
                                  <span className="font-medium text-blue-600">{attemptedIndices.length} / {totalItems}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Current Time:</span>
                                  <span className="font-medium text-blue-600">{currentQuestionTime}s</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Grade Level:</span>
                                  <span className="font-medium text-blue-600">{gradeName}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <Card className="p-6 rounded-3xl shadow-xl bg-white/95 flex flex-col items-center pb-32 sm:pb-8 h-full">
              {/* Progress Bar */}
              <div className="w-full mb-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-[#6B6FA3]">Question {currentIndex + 1} of {totalItems}</div>
                  <div className="flex items-center gap-4">
                    <div className="text-sm font-semibold text-[#55608A]">{currentMotivation}</div>
                    <div className="text-sm font-bold text-[#8E7BFF] bg-white/80 px-3 py-1 rounded-full border border-[#EDE9FF]">
                      Time: {currentQuestionTime}s
                    </div>
                  </div>
                </div>
                <div
                  className="w-full h-4 rounded-full overflow-hidden shadow-sm relative"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(progressPercentage)}
                  aria-label={`Progress: ${Math.round(progressPercentage)}%`}
                >
                  <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-[#F3E8FF] via-[#FFF7EE] to-[#F8FBFF] opacity-95 z-0" />
                  <div
                    style={{ width: `${progressPercentage}%` }}
                    className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] shadow-md transition-all z-10"
                  />
                </div>
              </div>

              {/* Character Avatar */}
              <div className="mb-6">
                <div className="flex items-center justify-center">
                  <div className="w-[10rem] h-[10rem] rounded-full overflow-hidden shadow-md border-2 border-gray-200">
                    <img src={login_hero_image} alt="Eko Icon" className="w-full h-full object-cover" />
                  </div>
                </div>
              </div>

              {/* Question Text */}
              <div className="mb-6 text-center w-full">
                <h2 className="text-2xl font-bold text-[#8E7BFF] mb-2 whitespace-pre-wrap break-words">{currentItem.question_text}</h2>
                <p className="text-gray-600">Choose the correct answer</p>
              </div>

              {/* Question Image (if available) */}
              {currentItem.image_url && (
                <div className="mb-6 w-full flex justify-center">
                  <div className="rounded-2xl overflow-hidden shadow-md border-2 border-gray-200 max-w-md w-full">
                    <ImageWithFallback
                      src={currentItem.image_url}
                      alt={currentItem.question_text}
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
              )}

              {/* MCQ Options */}
              <div className="w-full space-y-3 mb-6">
                {currentItem.options.map((option) => (
                  <button
                    key={option.index}
                    onClick={() => handleSelectAnswer(option.index)}
                    className={`w-full p-4 rounded-2xl text-left font-semibold transition-all duration-200 border-2 flex items-center gap-3 ${
                      selectedAnswerIndex === option.index
                        ? 'bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] text-white border-[#ECA7C1] shadow-lg'
                        : 'bg-white text-[#6B6FA3] border-[#EDE9FF] hover:border-[#F7B26A] hover:shadow-md'
                    }`}
                  >
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      selectedAnswerIndex === option.index
                        ? 'bg-white/30'
                        : 'bg-[#F3E8FF]'
                    }`}>
                      {String.fromCharCode(65 + option.index)}
                    </div>
                    <div className="flex-1">
                      {option.image_url ? (
                        <div className="flex items-center gap-2">
                          <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0">
                            <ImageWithFallback
                              src={option.image_url}
                              alt={option.text}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <span>{option.text}</span>
                        </div>
                      ) : (
                        <span>{option.text}</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 justify-center w-full mb-4 flex-wrap">
                <Button
                  onClick={handleSubmitAnswer}
                  disabled={selectedAnswerIndex === null || isSubmitting}
                  className={`min-w-[8rem] h-12 rounded-full text-lg font-bold shadow-lg transition-all ${
                    selectedAnswerIndex === null || isSubmitting
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'text-white bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] hover:brightness-95'
                  }`}
                >
                  {isSubmitting ? 'Submitting...' : currentIndex === totalItems - 1 ? 'Submit →' : 'Next →'}
                </Button>
                <Button
                  onClick={handleSkip}
                  size="lg"
                  variant="outline"
                  className="min-w-[6.5rem] h-12 rounded-full text-lg font-semibold border-2 border-[#EDE9FF] text-[#6B6FA3] bg-white"
                >
                  Skip
                </Button>
              </div>

              {/* Helper Message */}
              {selectedAnswerIndex === null && (
                <div className="text-center mb-4">
                  <p className="text-sm text-[#6B6FA3] bg-[#F6F6FF] border border-[#EDE9FF] rounded-xl px-4 py-2 inline-block">
                    💡 Select an answer to continue
                  </p>
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* Right Sidebar: Test Info (desktop only) */}
        <div className="right-col hidden lg:flex flex-col p-6 lg:p-8 bg-gradient-to-b from-blue-50 via-white to-blue-100 rounded-xl shadow-lg min-h-[500px] max-h-full overflow-auto border border-blue-200 lg:w-96 xl:w-[520px] lg:origin-top-right flex-shrink-0 unified-medium-panel" style={{ marginTop: '15px' }}>
          <div className="unified-medium-inner">
            <div className="mt-6 lg:mt-0 sticky top-20 w-full">
              <div className="w-full flex flex-col gap-4 lg:gap-6">
                <h2 className="font-bold text-xl lg:text-2xl mb-4 text-[#55608A]">Test Information</h2>
                <div className="space-y-4 text-base lg:text-lg">
                  <div>
                    <span className="font-semibold text-gray-700 lg:text-lg">Current Question</span><br />
                    <span className="text-blue-600 font-bold lg:text-lg">{currentIndex + 1} of {totalItems}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700 lg:text-lg">Grade Level</span><br />
                    <span className="text-orange-600 font-bold lg:text-lg">{gradeName}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700 lg:text-lg">Completed</span><br />
                    <span className="text-green-600 font-bold lg:text-lg">{attemptedIndices.length} / {totalItems}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700 lg:text-lg">Question Time</span><br />
                    <span className="text-purple-600 font-bold lg:text-lg">{currentQuestionTime}s</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700 lg:text-lg">Difficulty</span><br />
                    <span className="text-indigo-600 font-bold lg:text-lg capitalize">{currentItem.difficulty}</span>
                  </div>
                </div>
                <button onClick={handleLeave} className="w-full mb-2 py-3 lg:py-5 rounded-lg text-white text-base lg:text-xl lg:font-extrabold shadow transition" style={{ background: 'linear-gradient(90deg,#F7B26A,#ECA7C1)' }}>
                  End Test
                </button>

              </div>
            </div>
          </div>
        </div>
      </div>
      <footer style={{ marginTop: 10, color: '#6B6FA3', fontSize: 12, textAlign: 'center' }}>© The Dear Parent Project</footer>
    </div>
  );
};
